package com.flowyride.core.map

import com.flowyride.core.domain.analysis.SpeedStretch
import com.flowyride.core.domain.model.GeoPoint

/** Minimal GeoJSON encoding: avoids leaking a GeoJSON library type into the public API. */
internal object GeoJson {

    /** @param properties JSON object, read by the layers' filters. */
    fun lineString(points: List<GeoPoint>, properties: String = "{}"): String =
        feature("""{"type":"LineString","coordinates":[${points.joinToString(",") { it.position() }}]}""", properties)

    fun points(points: List<GeoPoint>): String =
        featureCollection(points.map { feature("""{"type":"Point","coordinates":${it.position()}}""") })

    fun featureCollection(features: List<String>): String =
        """{"type":"FeatureCollection","features":[${features.joinToString(",")}]}"""

    /** Points carrying an `id` property, read back from clicked features. */
    fun identifiedPoints(points: List<Pair<String, GeoPoint>>): String =
        """{"type":"FeatureCollection","features":[${
            points.joinToString(",") { (id, point) ->
                feature("""{"type":"Point","coordinates":${point.position()}}""", """{"$ID_PROPERTY":"${id.jsonEscaped()}"}""")
            }
        }]}"""

    /** One line per stretch, its band in [BAND_PROPERTY]. */
    fun speedStretches(track: List<GeoPoint>, stretches: List<SpeedStretch>): String = featureCollection(
        stretches.map { lineString(track.subList(it.startTrackIndex, it.endTrackIndex + 1), """{"$BAND_PROPERTY":"${it.band.name}"}""") },
    )

    const val ID_PROPERTY = "id"
    const val BAND_PROPERTY = "band"

    private fun feature(geometry: String, properties: String = "{}") =
        """{"type":"Feature","properties":$properties,"geometry":$geometry}"""

    private fun String.jsonEscaped() = replace("\\", "\\\\").replace("\"", "\\\"")

    private fun GeoPoint.position() = "[$longitude,$latitude]"
}

/** West, south, east, north. */
internal data class Bounds(val west: Double, val south: Double, val east: Double, val north: Double) {
    fun isInside(other: Bounds): Boolean =
        west >= other.west && east <= other.east && south >= other.south && north <= other.north

    fun expandedBy(degrees: Double) = Bounds(west - degrees, south - degrees, east + degrees, north + degrees)
}

internal fun List<GeoPoint>.bounds(): Bounds? {
    if (isEmpty()) return null
    return Bounds(
        west = minOf { it.longitude },
        south = minOf { it.latitude },
        east = maxOf { it.longitude },
        north = maxOf { it.latitude },
    )
}
